import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, Drawer, IconButton, MenuItem, Select, Stack, Tab, Tabs, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import OrdersService from '@api/Orders/OrderCtrl';
import UserService from '@api/Users/UserCtrl';
import { DeliveryType, Order, OrderStatus, PaymentMethods } from '@api/Orders/Order';
import { ProfileTypes, UserType } from '@api/Users/User';
import CashRegisterService from '@api/CashRegister/CashRegisterCtrl';
import CashFlowsService from '@api/CashFlows/CashFlowCtrl';
import { CashFlow, CashFlowType } from '@api/CashFlows/CashFlow';
import { useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import ReportTitle from '@components/SalesReport/ReportTitle';
import useOpenModal from '@hooks/useOpenModal';
import { ModalCashRegister } from '@components/SalesReport/Modals/ModalCashRegister';
import { ModalCashFlow } from '@components/SalesReport/Modals/ModalCashFlow';
import { ModalListOrders } from '@components/SalesReport/Modals/ModalListOrders';
import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import SalesByProducts from '@components/SalesReport/SalesByProducts';
import SalesPerHour from '@components/SalesReport/SalesPerHour';
import SalesByInputs from '@components/SalesReport/SalesByInputs';
import SalesByBranchOffices from '@components/SalesReport/SalesByBranchOffices';
import { FilterList } from '@mui/icons-material';
import FilterSidebar, { FilterValues } from '@screens/Dashboard/components/FilterSidebar';
import { CashRegister } from '@api/CashRegister/CashRegister';
import { PaymentMethod, getOrderPaidBy } from '@api/Orders/OrderPayments';
import { currencyFormatter } from '@utils/Numbers';
import { cashFlowsOfType, expenseGroupOf, isExpenseFlow, SHIPPING_CATEGORY_ID, sumAmounts } from '@utils/CashFlows';
import { useLocation } from 'react-router-dom';
import ExpensesTab, { ExpensesSubTab } from '@components/SalesReport/Expenses/ExpensesTab';
import {
    describePurchaseQuantity,
    EMPTY_EXPENSE_FILTERS,
    EXPENSE_STATUS_LABELS,
    ExpenseListFilters,
    expenseStatusOf
} from '@utils/Expenses';
import { getExpenseGroupName } from '@api/ExpenseCategories/ExpenseCategory';
import { canCurrentUser } from '@utils/Permissions';
import { seesAllBranches } from '@utils/Permissions/Capabilities';
import { usePermissionSubject, useVisibleBranchOffices } from '@hooks/usePermissionSubject';
import { Capability } from '@utils/Permissions/Capabilities';
import useUserNames from '@hooks/useUserNames';

type ReportTab = 'resumen' | 'productos' | 'insumos' | 'sucursales' | 'gastos';
const SUB_TABS: ExpensesSubTab[] = ['lista', 'resultados', 'fijos', 'categorias'];

const today = new Date();
today.setHours(0, 0, 0, 0);

const endToday = new Date();
endToday.setHours(23, 59, 59, 59);

const CollaboratorSalesReportScreen: React.FunctionComponent = () => {
    const { branchOfficeSelected } = useSelector((state: RootReducer) => state.auth);
    const { salesChannels } = useSelector((state: RootReducer) => state.krispis);
    const subject = usePermissionSubject();
    const branchOffices = useVisibleBranchOffices();

    // Informe de ventas ÚNICO para todos los perfiles. El filtro de sucursal aparece
    // según la asignación de QUIEN OPERA (no el rol; en "Ver como", la sucursal del
    // sidebar): con 'all' o varias sucursales puede filtrar; con una sola queda fija.
    // El estatus NO es filtrable: la conciliación de caja depende de órdenes Completadas.
    const hasAllBranches = seesAllBranches(subject);
    const canFilterBranch = hasAllBranches || branchOffices.length > 1;

    const initialFilters: FilterValues = useMemo(
        () => ({
            startDate: today,
            endDate: endToday,
            status: OrderStatus.Completed,
            // Default: la sucursal seleccionada globalmente (también para admin);
            // "Todas" es una elección manual en el filtro.
            branchOfficeId: branchOfficeSelected?.id || branchOffices[0]?.id || 'all',
            salesChannel: 'all',
            deliveryType: 'all',
            hasUber: 'all',
            collaboratorId: 'all'
        }),
        [branchOfficeSelected, branchOffices]
    );
    const ordersService = useMemo(() => new OrdersService(), []);
    const usersService = useMemo(() => new UserService(), []);
    const cashRegisterService = useMemo(() => new CashRegisterService(), []);
    const [cashRegisters, setCashRegisters] = useState<CashRegister[]>([]);
    const [selectedCashRegister, setSelectedCashRegister] = useState<CashRegister | undefined>(undefined);
    const cashFlowsService = useMemo(() => new CashFlowsService(), []);
    const [orders, setOrders] = useState<Order[]>([]);
    const [collaborators, setCollaborators] = useState<UserType[]>([]);
    const [cashFlow, setCashFlow] = useState<CashFlow[]>([]);
    const [cashFlowTypeSelected, setCashFlowTypeSelected] = useState<CashFlowType>(CashFlowType.Output);
    const [paymentMethodTypeSelected, setPaymentMethodTypeSelected] = useState<PaymentMethods>(PaymentMethods.Cash);
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
    const [detailTab, setDetailTab] = useState<ReportTab>('resumen');
    const [expensesSub, setExpensesSub] = useState<ExpensesSubTab>('lista');
    const [expenseFilters, setExpenseFilters] = useState<ExpenseListFilters>(EMPTY_EXPENSE_FILTERS);
    const location = useLocation();
    const [filters, setFilters] = useState<FilterValues>(initialFilters);
    const [tempFilters, setTempFilters] = useState<FilterValues>(initialFilters);
    const modalCashRegister = useOpenModal();
    const modalCashFlow = useOpenModal();
    const modalListOrders = useOpenModal();

    const handleOpenFilterDrawer = () => {
        setTempFilters(filters);
        setFilterDrawerOpen(true);
    };

    const handleApplyFilters = () => {
        setFilters(tempFilters);
        setFilterDrawerOpen(false);
    };

    const handleClearFilters = () => {
        setTempFilters(initialFilters);
        setFilters(initialFilters);
        setFilterDrawerOpen(false);
    };

    const getCollaborators = async () => {
        try {
            const collabs = await usersService.getUsersByProfile(ProfileTypes.Collaborator);
            setCollaborators(collabs);
        } catch (error: any) {
            showAlertError(error);
        }
    };

    // "Otros ingresos" sigue en su modal. Los gastos y los envíos se ven en la pestaña
    // Gastos (plan de Gastos §5.2): ahí se cancelan con motivo y PIN, nunca se borran.
    const onOpenCashFlow = (type: CashFlowType) => {
        if (type !== CashFlowType.Input && canCurrentUser(Capability.ExpenseListView)) {
            setExpenseFilters({
                ...EMPTY_EXPENSE_FILTERS,
                categoryKey: type === CashFlowType.Shipping ? SHIPPING_CATEGORY_ID : 'all'
            });
            setExpensesSub('lista');
            setDetailTab('gastos');
            return;
        }
        setCashFlowTypeSelected(type);
        modalCashFlow.toggle();
    };

    const onOpenModalOrders = (type: PaymentMethods) => {
        setPaymentMethodTypeSelected(type);
        modalListOrders.toggle();
    };
    const getGrossSales = () => {
        return orders.reduce((total, order) => {
            return total + order.total || 0;
        }, 0);
    };

    const getNetSales = () => {
        return orders.reduce((total, order) => {
            return total + order.total - order.shippingCost || 0;
        }, 0);
    };

    // Los medios de pago se leen por `getOrderPaidBy`: entiende el modelo nuevo
    // (`order.payments[]`) y el viejo (los 3 escalares). De paso arregla un bug
    // de precedencia que tenía este acumulador — `total + order.cashAmount || 0`
    // se evalúa como `(total + x) || 0`, así que UN campo indefinido reiniciaba
    // la suma a 0 y el informe subreportaba en silencio.
    const sumPaidBy = (method: PaymentMethod) =>
        orders.reduce((total, order) => total + getOrderPaidBy(order, method), 0);

    const getCashSales = () => sumPaidBy(PaymentMethod.Cash);

    const getCreditCardSales = () => sumPaidBy(PaymentMethod.CreditCard);

    const getBankTransferSales = () => sumPaidBy(PaymentMethod.BankTransfer);

    const getPointsSales = () => {
        return orders.reduce((total, order) => {
            return total + order.pointsAmount || 0;
        }, 0);
    };

    // Propinas (espejo POS, D2): tipAmount NO está dentro de `total` (la venta
    // bruta no lo trae), pero los montos de pago SÍ — por eso "recibido en
    // efectivo/tarjeta" incluye propinas y esta fila las hace visibles para
    // que el negocio pueda repartirlas.
    const getTips = () => {
        return orders.reduce((total, order) => {
            return total + (order.tipAmount || 0);
        }, 0);
    };

    const getAverageTicket = () => {
        return getGrossSales() / orders.length || 0;
    };
    // Lo que movió efectivo de la caja, por renglón: la regla vive en `@utils/CashFlows`.
    const getCashFlowOutputs = () => sumAmounts(cashFlowsOfType(cashFlow, CashFlowType.Output));

    const getCashFlowInputs = () => sumAmounts(cashFlowsOfType(cashFlow, CashFlowType.Input));

    const getCashFlowShipping = () => sumAmounts(cashFlowsOfType(cashFlow, CashFlowType.Shipping));

    const getFinalCashRegister = () => {
        return (
            getCashRegisterAmount() +
            getCashSales() +
            getCashFlowInputs() -
            getCashFlowOutputs() -
            getCashFlowShipping()
        );
    };

    const getCashRegisterAmount = () => {
        if (!selectedCashRegister) {
            return cashRegisters.reduce((total, cashRegister) => total + cashRegister.initialCash, 0);
        }
        return selectedCashRegister.initialCash || 0;
    };

    const getData = async () => {
        showLoader();
        try {
            // Cajas, flujos y órdenes siguen la MISMA sucursal del filtro para que la
            // conciliación cuadre. Con "Todas" + usuario restringido, se limita en
            // cliente a sus sucursales asignadas (el catálogo ya viene scopeado).
            const branchFilter = filters.branchOfficeId;
            const isAllowedBranch = (orderBranchId?: string) =>
                hasAllBranches || branchOffices.some((branch) => branch.id === orderBranchId);

            const cashFlowData = await cashFlowsService.getCashFlows(filters.startDate, filters.endDate, branchFilter);
            const cashRegistersData = await cashRegisterService.getCashRegistersByDate(filters.startDate, branchFilter);
            setCashFlow(
                branchFilter === 'all' ? cashFlowData.filter((c) => isAllowedBranch(c.branchOfficeId)) : cashFlowData
            );
            const visibleCashRegisters =
                branchFilter === 'all'
                    ? cashRegistersData.filter((c) => isAllowedBranch(c.branchOfficeId))
                    : cashRegistersData;
            setCashRegisters(visibleCashRegisters);
            // Si la caja seleccionada ya no pertenece a la sucursal filtrada, deseleccionar
            if (selectedCashRegister && !visibleCashRegisters.some((c) => c.id === selectedCashRegister.id)) {
                setSelectedCashRegister(undefined);
            }

            const ordersData = await ordersService.getOrders(
                OrderStatus.Completed,
                filters.startDate,
                filters.endDate,
                branchFilter !== 'all' ? branchFilter : undefined,
                filters.salesChannel && filters.salesChannel !== 'all' ? filters.salesChannel : undefined,
                filters.deliveryType !== 'all' ? (filters.deliveryType as DeliveryType) : undefined
            );

            // Filtros aplicados en cliente (no soportados por la query de Firestore)
            const filteredOrders = ordersData.filter((order) => {
                if (branchFilter === 'all' && !isAllowedBranch(order.branchOfficeId)) return false;
                if (filters.hasUber === 'yes' && !order.uberDeliveryId) return false;
                if (filters.hasUber === 'no' && order.uberDeliveryId) return false;
                if (filters.collaboratorId !== 'all' && order.collaboratorId !== filters.collaboratorId) {
                    return false;
                }
                return true;
            });

            setOrders(filteredOrders);
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };
    const onDownload = () => {
        const ordersData = orders
            .filter((order) => !order.salesChannel)
            .map((order) => {
                return {
                    Ticket: order.shortId,
                    'Fecha y hora': format(order.date.toDate(), 'dd-MM-yy pp'),
                    Cliente: order.customer?.profile?.name,
                    Colaborador: order.collaboratorData?.profile.name,
                    Subtotal: order.subtotal || 0,
                    Promocion: order.promotion || 0,
                    Envio: order.shippingCost || 0,
                    Total: order.total || 0,
                    Propina: order.tipAmount || 0,
                    Efectivo: getOrderPaidBy(order, PaymentMethod.Cash),
                    Transferencia: getOrderPaidBy(order, PaymentMethod.BankTransfer),
                    Tarjeta: getOrderPaidBy(order, PaymentMethod.CreditCard)
                };
            });
        const ordersSalesChannelsData = orders
            .filter((order) => order.salesChannel)
            .map((order) => {
                const commissionPercent = order.salesChannel?.creditCardCommission || 0;
                const cardPaid = getOrderPaidBy(order, PaymentMethod.CreditCard);
                const commissionValue = cardPaid * (commissionPercent > 0 ? commissionPercent / 100 : 0);
                const totalWithoutCommission = cardPaid - commissionValue;
                return {
                    Ticket: order.shortId,
                    'Fecha y hora': format(order.date.toDate(), 'dd-MM-yy pp'),
                    Canal: order.salesChannel?.name || '',
                    Cliente: order.customer?.profile?.name,
                    Colaborador: order.collaboratorData?.profile.name,
                    Subtotal: order.subtotal || 0,
                    Promocion: order.promotion || 0,
                    Envio: order.shippingCost || 0,
                    Total: order.total || 0,
                    Propina: order.tipAmount || 0,
                    Efectivo: getOrderPaidBy(order, PaymentMethod.Cash),
                    Transferencia: getOrderPaidBy(order, PaymentMethod.BankTransfer),
                    Tarjeta: getOrderPaidBy(order, PaymentMethod.CreditCard),
                    Comision: commissionValue,
                    'Tarjeta sin comisión': totalWithoutCommission
                };
            });
        // Movimientos de caja que no son el efectivo de un pedido: gastos (con su
        // categoría, grupo, nómina, si salieron de la caja y su estado) y otros ingresos.
        const branchNameOf = (id?: string) => branchOffices.find((branch) => branch.id === id)?.name || '';
        const cashFlowData = cashFlow
            .filter((c) => !c.orderId)
            .map((flow) => {
                const expense = isExpenseFlow(flow);
                return {
                    Tipo: expense ? 'Gasto' : 'Ingreso',
                    'Fecha y hora': format(flow.date.toDate(), 'dd-MM-yy pp'),
                    Sucursal: branchNameOf(flow.branchOfficeId),
                    Categoria: expense ? flow.categoryName || (flow.categoryId ? '' : 'Sin categoría') : '',
                    Grupo: expense ? getExpenseGroupName(expenseGroupOf(flow)) : '',
                    Descripcion: flow.description,
                    // Compra en la calle (plan de inventario F1): cuánto entró, con su unidad
                    Cantidad: describePurchaseQuantity(flow),
                    Colaborador: flow.payrollUserName || '',
                    Mes: flow.period || '',
                    'Salio de caja': flow.paidFromCash === false ? 'No' : 'Sí',
                    Capturo: (flow.userId && userNames[flow.userId]) || '',
                    Estado: expense ? EXPENSE_STATUS_LABELS[expenseStatusOf(flow)] : '',
                    'Motivo de cancelacion': flow.deleteReason || '',
                    Monto: flow.amount
                };
            });
        const ws = XLSX.utils.json_to_sheet(ordersData);
        const ws2 = XLSX.utils.json_to_sheet(ordersSalesChannelsData);
        const ws3 = XLSX.utils.json_to_sheet(cashFlowData);
        const wscols = [];
        const ws2cols = [];
        const ws3cols = [];
        for (let i = 0; i < 11; i++) {
            wscols.push({ wch: 20 });
        }
        for (let i = 0; i < 14; i++) {
            ws2cols.push({ wch: 20 });
        }
        for (let i = 0; i < 14; i++) {
            ws3cols.push({ wch: 18 });
        }
        ws['!cols'] = wscols;
        ws2['!cols'] = ws2cols;
        ws3['!cols'] = ws3cols;
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Ordenes');
        XLSX.utils.book_append_sheet(wb, ws2, 'Ordenes de canales');
        XLSX.utils.book_append_sheet(wb, ws3, 'Flujo de caja');
        XLSX.writeFile(wb, `Reporte - ${format(new Date(), 'dd-MM-yyy')}.xlsx`);
    };

    useEffect(() => {
        void getCollaborators();
    }, []);

    // Un aviso de la campana puede abrir directo una sub-pestaña: `?tab=gastos&sub=fijos`.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('tab') !== 'gastos') return;
        setDetailTab('gastos');
        const sub = params.get('sub');
        const wanted = sub === 'lista' || sub === 'resultados' || sub === 'fijos' || sub === 'categorias' ? sub : undefined;
        if (wanted) setExpensesSub(SUB_TABS.includes(wanted) ? wanted : 'lista');
    }, [location.search]);

    // Quién capturó cada movimiento, para el Excel (una lectura por persona, con caché).
    const userNames = useUserNames(cashFlow.filter((flow) => !flow.orderId).map((flow) => flow.userId));

    useEffect(() => {
        void getData();
    }, [filters]);

    // Layout estándar con tabs (patrón CedisScreen): tabs hasta arriba y card a todo
    // el alto con scroll interno. "Ventas por sucursal" solo con el filtro en "Todas".
    const tabs = useMemo(() => {
        const list: { value: ReportTab; label: string }[] = [
            { value: 'resumen', label: 'Resumen general' },
            { value: 'productos', label: 'Ventas por producto' },
            { value: 'insumos', label: 'Ventas por insumo' }
        ];
        if (filters.branchOfficeId === 'all') list.push({ value: 'sucursales', label: 'Ventas por sucursal' });
        if (canCurrentUser(Capability.ExpenseListView)) list.push({ value: 'gastos', label: 'Gastos' });
        return list;
    }, [filters.branchOfficeId]);
    const activeDetailTab: ReportTab = tabs.some((tab) => tab.value === detailTab) ? detailTab : 'resumen';

    return (
        <Stack height="100%" p={2} spacing={2}>
            <Drawer
                anchor="left"
                open={filterDrawerOpen}
                onClose={() => setFilterDrawerOpen(false)}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: 350,
                        p: 3,
                        boxSizing: 'border-box'
                    }
                }}
            >
                <FilterSidebar
                    filters={tempFilters}
                    onFilterChange={setTempFilters}
                    branchOffices={branchOffices}
                    collaborators={collaborators}
                    salesChannels={salesChannels}
                    showBranchFilter={canFilterBranch}
                    allowAllBranches={hasAllBranches}
                    showStatusFilter={false}
                    onClear={handleClearFilters}
                    onApply={handleApplyFilters}
                />
            </Drawer>

            {/* Tabs hasta arriba + acciones globales (filtros/periodo/Excel accesibles desde cualquier tab) */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                <Tabs
                    value={activeDetailTab}
                    onChange={(_, value) => setDetailTab(value)}
                    variant="scrollable"
                    scrollButtons="auto"
                    textColor="primary"
                    indicatorColor="primary"
                >
                    {tabs.map((tab) => (
                        <Tab key={tab.value} value={tab.value} label={tab.label} sx={{ fontWeight: 600 }} />
                    ))}
                </Tabs>
                <Stack direction="row" spacing={2} alignItems="center">
                    <IconButton
                        color="primary"
                        onClick={handleOpenFilterDrawer}
                        sx={{ border: '1px solid', borderColor: 'divider' }}
                    >
                        <FilterList />
                    </IconButton>
                    <Stack>
                        <Typography variant="caption" color="text.secondary">
                            Periodo
                        </Typography>
                        <Typography variant="body2" fontWeight={600} noWrap>
                            {`${format(filters.startDate, 'dd MMM yyy')} — ${format(filters.endDate, 'dd MMM yyy')}`}
                        </Typography>
                    </Stack>
                    <Button variant="contained" onClick={onDownload}>
                        Excel
                    </Button>
                </Stack>
            </Stack>

            {/* Card principal a todo el alto; el scroll vive DENTRO de cada tab */}
            <Stack component={Card} flex={1} minHeight={0} overflow="hidden">
                {activeDetailTab === 'resumen' && (
                    <Stack height="100%" spacing={4} sx={{ overflowY: 'auto' }}>
                        <Select
                            id="cashRegisterUser"
                            size="small"
                            sx={{ maxWidth: 360 }}
                            value={selectedCashRegister?.id || 'all'}
                            onChange={(e) => {
                                const item = cashRegisters.find((c) => c.id === e.target.value);
                                setSelectedCashRegister(item);
                            }}
                        >
                            <MenuItem value={'all'}>Todas las cajas</MenuItem>
                            {cashRegisters.map((cashRegister, index) => (
                                <MenuItem key={index} value={cashRegister.id}>
                                    {`Caja ${index + 1} - ${cashRegister.username}`}
                                </MenuItem>
                            ))}
                        </Select>
                        <Grid container spacing={4}>
                            <Grid item xs={6} md={2}>
                                <Stack>
                                    <Typography color="gray">Venta bruta</Typography>
                                    <Typography color="green" fontSize={20} fontWeight={700}>
                                        {currencyFormatter(getGrossSales())}
                                    </Typography>
                                </Stack>
                            </Grid>
                            <Grid item xs={6} md={2}>
                                <Stack>
                                    <Typography color="gray">Venta neta</Typography>
                                    <Typography fontSize={20} fontWeight={700}>
                                        {currencyFormatter(getNetSales())}
                                    </Typography>
                                </Stack>
                            </Grid>
                            <Grid item xs={6} md={2}>
                                <Stack>
                                    <ReportTitle
                                        text="Venta en efectivo"
                                        showInfo
                                        onPressInfo={() => onOpenModalOrders(PaymentMethods.Cash)}
                                    />
                                    <Typography fontSize={20} fontWeight={700}>
                                        {currencyFormatter(getCashSales())}
                                    </Typography>
                                </Stack>
                            </Grid>
                            <Grid item xs={6} md={2}>
                                <Stack>
                                    <ReportTitle
                                        text="Venta con tarjeta"
                                        showInfo
                                        onPressInfo={() => onOpenModalOrders(PaymentMethods.CreditCard)}
                                    />
                                    <Typography fontSize={20} fontWeight={700}>
                                        {currencyFormatter(getCreditCardSales())}
                                    </Typography>
                                </Stack>
                            </Grid>
                            <Grid item xs={6} md={2}>
                                <Stack>
                                    <ReportTitle
                                        text="Venta con transferencia"
                                        showInfo
                                        onPressInfo={() => onOpenModalOrders(PaymentMethods.BankTransfer)}
                                    />
                                    <Typography fontSize={20} fontWeight={700}>
                                        {currencyFormatter(getBankTransferSales())}
                                    </Typography>
                                </Stack>
                            </Grid>
                            <Grid item xs={6} md={2}>
                                <Stack>
                                    <Typography variant="body1" color="gray">
                                        Puntos KRISPIS
                                    </Typography>
                                    <Typography fontSize={20} fontWeight={700}>
                                        {currencyFormatter(getPointsSales())}
                                    </Typography>
                                </Stack>
                            </Grid>
                            <Grid item xs={6} md={2}>
                                <Stack>
                                    <Typography variant="body1" color="gray">
                                        Propinas
                                    </Typography>
                                    <Typography fontSize={20} fontWeight={700}>
                                        {currencyFormatter(getTips())}
                                    </Typography>
                                </Stack>
                            </Grid>
                            <Grid item xs={6} md={2}>
                                <Stack>
                                    <ReportTitle
                                        text="Efectivo en caja"
                                        showInfo={!!selectedCashRegister}
                                        onPressInfo={() => modalCashRegister.toggle()}
                                    />
                                    <Typography fontSize={20} fontWeight={700}>
                                        {currencyFormatter(getCashRegisterAmount())}
                                    </Typography>
                                </Stack>
                            </Grid>
                            <Grid item xs={6} md={2}>
                                <Stack>
                                    <ReportTitle
                                        text="Otros gastos"
                                        showInfo
                                        onPressInfo={() => onOpenCashFlow(CashFlowType.Output)}
                                    />
                                    <Typography fontSize={20} fontWeight={700}>
                                        {currencyFormatter(getCashFlowOutputs())}
                                    </Typography>
                                </Stack>
                            </Grid>
                            <Grid item xs={6} md={2}>
                                <Stack>
                                    <ReportTitle
                                        text="Otros ingresos"
                                        showInfo
                                        onPressInfo={() => onOpenCashFlow(CashFlowType.Input)}
                                    />
                                    <Typography fontSize={20} fontWeight={700}>
                                        {currencyFormatter(getCashFlowInputs())}
                                    </Typography>
                                </Stack>
                            </Grid>
                            <Grid item xs={6} md={2}>
                                <Stack>
                                    <ReportTitle
                                        text="Gastos de envio"
                                        showInfo
                                        onPressInfo={() => onOpenCashFlow(CashFlowType.Shipping)}
                                    />
                                    <Typography fontSize={20} fontWeight={700}>
                                        {currencyFormatter(getCashFlowShipping())}
                                    </Typography>
                                </Stack>
                            </Grid>
                            <Grid item xs={6} md={2}>
                                <Stack>
                                    <Typography color="gray">Ticket promedio</Typography>
                                    <Typography fontSize={20} fontWeight={700}>
                                        {currencyFormatter(getAverageTicket())}
                                    </Typography>
                                </Stack>
                            </Grid>
                            {!selectedCashRegister && (
                                <Grid item xs={6} md={2}>
                                    <Stack>
                                        <Typography color="gray">Efectivo final en cajas</Typography>
                                        <Typography fontSize={20} fontWeight={700}>
                                            {currencyFormatter(getFinalCashRegister())}
                                        </Typography>
                                    </Stack>
                                </Grid>
                            )}
                        </Grid>
                        <SalesPerHour orders={orders} />
                    </Stack>
                )}
                {activeDetailTab === 'productos' && (
                    <Box height="100%">
                        <SalesByProducts orders={orders} />
                    </Box>
                )}
                {activeDetailTab === 'insumos' && (
                    <Box height="100%">
                        <SalesByInputs orders={orders} />
                    </Box>
                )}
                {activeDetailTab === 'sucursales' && (
                    <Box height="100%">
                        <SalesByBranchOffices orders={orders} />
                    </Box>
                )}
                {activeDetailTab === 'gastos' && (
                    <ExpensesTab
                        cashFlows={cashFlow}
                        branchOfficeId={filters.branchOfficeId}
                        sub={expensesSub}
                        onSubChange={setExpensesSub}
                        filters={expenseFilters}
                        onFiltersChange={setExpenseFilters}
                        onChanged={() => void getData()}
                    />
                )}
            </Stack>
            {selectedCashRegister && (
                <ModalCashRegister modalCashRegister={modalCashRegister} cashRegister={selectedCashRegister} />
            )}
            <ModalCashFlow
                modalCashFlow={modalCashFlow}
                cashFlowType={cashFlowTypeSelected}
                startDate={filters.startDate}
                endDate={filters.endDate}
                branchOfficeId={filters.branchOfficeId}
            />
            <ModalListOrders
                modalListOrders={modalListOrders}
                paymentMethodType={paymentMethodTypeSelected}
                startDate={filters.startDate}
                endDate={filters.endDate}
                branchOfficeId={filters.branchOfficeId}
            />
        </Stack>
    );
};

export default CollaboratorSalesReportScreen;
